import { getPartnerToken } from '@/services/auth/partnerSessionStorage';
import { createPrintAgentBinding } from './printAgentBinding';
import { PRINT_EVENT_KEYS } from '@/services/printing/printJobService';
import { submitRestaurantPrint, usesRestaurantPrintTransport, restaurantPrintMode } from '@/services/printing/restaurantPrintTransport';
import { getOfflineSession, isOfflineSessionActive } from '@/services/offline/offlineUserAccess';
import { getPartnerSessionCache } from '@/services/offline/partnerSessionCache';
import {
  cachedPrintBeepFor,
  cachedPrintCodePageFor,
  cachedPrintCommandDesignFor,
  cachedPrintCommandLayoutFor,
  cachedPrintCommandServiceLabelFor,
  getRestaurantPrintSettings,
  normalizeBeepCommand,
  normalizeLineWidth,
} from '@/services/printing/printSettingsService';

const PRINT_AGENT_BASE_URL = 'http://127.0.0.1:8751';
const DEFAULT_TIMEOUT_MS = 30000;
const PRINT_AGENT_CONFIG_CACHE_KEY = 'ewu_print_agent_config_cache';
const PRINT_ASSIGNMENT_TARGET_PREFIX = 'assignment:';
export const VIRTUAL_PRINT_ACTIONS_EVENT = 'ewu:virtual-print-actions';

export const DEFAULT_PRINT_SERVICE_ASSIGNMENTS = Object.freeze({
  kitchen_order: null,
  partner_ws: null,
  pos_order: null,
  account: null,
});

export const PRINT_SERVICE_ASSIGNMENT_OPTIONS = Object.freeze([
  {
    key: 'kitchen_order',
    label: 'Cocina',
    description: 'Comandas manuales y automáticas del agente.',
  },
  {
    key: 'pos_order',
    label: 'Caja',
    description: 'Tickets POS y tickets de caja.',
  },
  {
    key: 'account',
    label: 'Facturas completas',
    description: 'Cuenta completa e impresión final para el cliente.',
  },
]);

export const PRINT_EVENT_OPTIONS = Object.freeze([
  {
    key: PRINT_EVENT_KEYS.QR_ORDER_CREATED,
    label: 'Si se realiza pedido en QR',
    description: 'Pedido recibido por el agente desde la web del cliente o canal QR.',
  },
  {
    key: PRINT_EVENT_KEYS.POS_ORDER_CREATED,
    label: 'Si se crea comanda desde TPV',
    description: 'Comanda creada o confirmada desde Punto de Venta.',
  },
  {
    key: PRINT_EVENT_KEYS.MANUAL_KITCHEN_PRINT,
    label: 'Si se imprime una comanda manualmente',
    description: 'Impresion manual desde el apartado de comandas.',
  },
  {
    key: PRINT_EVENT_KEYS.ACCOUNT_PRINT,
    label: 'Si se imprime una cuenta desde TPV',
    description: 'Cuenta o cuenta por comensal lanzada desde Punto de Venta.',
  },
  {
    key: PRINT_EVENT_KEYS.INVOICE_SIMPLIFIED_PRINT,
    label: 'Si se imprime un ticket simplificado',
    description: 'Impresion documental desde el modulo de facturas.',
  },
  {
    key: PRINT_EVENT_KEYS.INVOICE_COMPLETE_PRINT,
    label: 'Si se imprime una factura completa',
    description: 'Factura completa emitida desde el modulo de facturas.',
  },
]);

export const DEFAULT_PRINT_EVENT_RULES = Object.freeze(
  PRINT_EVENT_OPTIONS.reduce((rules, option) => {
    rules[option.key] = [];
    return rules;
  }, {})
);

let agentRequestChain = Promise.resolve();

function getAgentToken() {
  return process.env.VUE_APP_EWU_PRINT_AGENT_TOKEN || '';
}

function isBrowser() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function publishVirtualPrintActions(actions = []) {
  if (!isBrowser() || !Array.isArray(actions) || !actions.length) return;
  window.dispatchEvent(new CustomEvent(VIRTUAL_PRINT_ACTIONS_EVENT, {
    detail: actions,
  }));
}

function normalizeText(value) {
  return String(value || '').trim();
}

function normalizeServiceId(value) {
  return normalizeText(value) || null;
}

function normalizeEventKey(value) {
  return normalizeText(value) || null;
}

function isVirtualPrinterName(printerName) {
  const normalized = normalizeText(printerName).toLowerCase();
  if (!normalized) return false;

  return [
    'microsoft print to pdf',
    'microsoft xps document writer',
    'onenote',
    'fax',
    'send to onenote',
    'virtual',
    'xps',
    'pdf',
  ].some((signature) => normalized.includes(signature));
}

function parseAssignmentTargetKey(value) {
  const normalizedValue = normalizeText(value).toLowerCase();
  if (!normalizedValue.startsWith(PRINT_ASSIGNMENT_TARGET_PREFIX)) {
    return null;
  }

  const assignmentKey = normalizedValue.slice(PRINT_ASSIGNMENT_TARGET_PREFIX.length);
  return assignmentKey || null;
}

function normalizePrintServices(printServices = []) {
  if (!Array.isArray(printServices)) return [];

  const normalized = [];
  const seenIds = new Set();

  printServices.forEach((service) => {
    if (!service || typeof service !== 'object') return;

    const id = normalizeServiceId(
      service.id
      || service.service_id
      || service.identifier
      || service.name
    );

    if (!id || seenIds.has(id)) return;

    normalized.push({
      id,
      name: normalizeText(service.name || id) || id,
      printer_name: normalizeText(service.printer_name) || null,
      prints_commands: service.prints_commands !== false,
      beep_command: normalizeBeepCommand(service.beep_command),
      line_width: normalizeLineWidth(service.line_width),
    });
    seenIds.add(id);
  });

  return normalized;
}

function normalizePrintServiceAssignments(assignments = {}) {
  const normalized = {
    ...DEFAULT_PRINT_SERVICE_ASSIGNMENTS,
  };

  if (assignments && typeof assignments === 'object') {
    Object.entries(assignments).forEach(([key, value]) => {
      const normalizedKey = normalizeText(key);
      if (!normalizedKey) return;
      normalized[normalizedKey] = normalizeServiceId(value);
    });
  }

  if (!normalized.partner_ws) {
    normalized.partner_ws = normalized.kitchen_order || null;
  }

  return normalized;
}

function normalizePrintEventRuleServiceIds(value) {
  let rawValues = [];

  if (Array.isArray(value)) {
    rawValues = value;
  } else if (typeof value === 'string') {
    rawValues = [value];
  } else if (value && typeof value === 'object') {
    if (Array.isArray(value.service_ids)) {
      rawValues = value.service_ids;
    } else if (Array.isArray(value.services)) {
      rawValues = value.services;
    }
  }

  return [...new Set(rawValues.map((serviceId) => normalizeServiceId(serviceId)).filter(Boolean))];
}

function resolveConfiguredTargetServiceId(targetId, config = getCachedPrintAgentConfig()) {
  const normalizedTargetId = normalizeServiceId(targetId);
  if (!normalizedTargetId) return null;

  const assignmentKey = parseAssignmentTargetKey(normalizedTargetId);
  if (!assignmentKey) {
    return normalizedTargetId;
  }

  const normalizedConfig = normalizePrintAgentConfig(config);
  return normalizeServiceId(normalizedConfig.print_service_assignments?.[assignmentKey]);
}

function normalizePrintEventRules(rules = {}) {
  const normalized = {
    ...DEFAULT_PRINT_EVENT_RULES,
  };

  if (rules && typeof rules === 'object') {
    Object.entries(rules).forEach(([eventKey, serviceIds]) => {
      const normalizedEventKey = normalizeEventKey(eventKey);
      if (!normalizedEventKey) return;
      normalized[normalizedEventKey] = normalizePrintEventRuleServiceIds(serviceIds);
    });
  }

  return normalized;
}

function normalizeCommandCategoryAssignments(assignments = {}, serviceIds = null) {
  if (!assignments || typeof assignments !== 'object' || Array.isArray(assignments)) return {};

  return Object.fromEntries(
    Object.entries(assignments)
      .map(([categoryId, assignedServiceIds]) => {
        const normalizedCategoryId = normalizeText(categoryId);
        const normalizedServiceIds = normalizePrintEventRuleServiceIds(assignedServiceIds)
          .filter((serviceId) => !serviceIds || serviceIds.has(serviceId));
        return normalizedCategoryId ? [normalizedCategoryId, normalizedServiceIds] : null;
      })
      .filter(Boolean)
  );
}

export function normalizePrintAgentConfig(config = {}) {
  const printServices = normalizePrintServices(config.print_services);
  const serviceIds = new Set(printServices.map((service) => service.id));
  const printServiceAssignments = normalizePrintServiceAssignments(config.print_service_assignments);

  Object.entries(printServiceAssignments).forEach(([assignmentKey, serviceId]) => {
    if (serviceId && !serviceIds.has(serviceId)) {
      printServiceAssignments[assignmentKey] = null;
    }
  });

  return {
    installation_id: normalizeText(config.installation_id),
    binding: config.binding || {},
    profile_key: config.profile_key || '',
    has_legacy_profile: Boolean(config.has_legacy_profile),
    backend_url: normalizeText(config.backend_url).replace(/\/+$/, ''),
    partner_token: normalizeText(config.partner_token),
    refresh_token: normalizeText(config.refresh_token),
    printer_name: normalizeText(config.printer_name) || null,
    print_services: printServices,
    print_service_assignments: printServiceAssignments,
    print_event_rules: normalizePrintEventRules(config.print_event_rules),
    command_category_assignments: normalizeCommandCategoryAssignments(
      config.command_category_assignments,
      serviceIds
    ),
    auto_print_enabled: config.auto_print_enabled !== false,
    restaurant_name: normalizeText(config.restaurant_name),
    cash_drawer: {
      enabled: Boolean(config.cash_drawer?.enabled),
      printer_name: normalizeText(config.cash_drawer?.printer_name) || null,
      connector_pin: Number(config.cash_drawer?.connector_pin) === 5 ? 5 : 2,
      pulse_on_ms: Math.max(2, Math.min(510, Number(config.cash_drawer?.pulse_on_ms) || 50)),
      pulse_off_ms: Math.max(4, Math.min(510, Number(config.cash_drawer?.pulse_off_ms) || 250)),
    },
  };
}

export function getConfiguredPrintEventServiceIds(eventKey, config = getCachedPrintAgentConfig()) {
  const normalizedEventKey = normalizeEventKey(eventKey);
  if (!normalizedEventKey) return [];

  const normalizedConfig = normalizePrintAgentConfig(config);
  return normalizePrintEventRuleServiceIds(normalizedConfig.print_event_rules?.[normalizedEventKey]);
}

export function hasConfiguredPrintFlow(eventKey, config = getCachedPrintAgentConfig()) {
  return getConfiguredPrintEventServiceIds(eventKey, config).length > 0;
}

export function buildPrintRequestsForEvent(payload = {}, eventKey = null) {
  const normalizedEventKey = normalizeEventKey(eventKey);
  const metadata = payload.metadata && typeof payload.metadata === 'object'
    ? { ...payload.metadata }
    : {};

  if (normalizedEventKey) {
    metadata.print_event = normalizedEventKey;
  }

  const requestPayload = {
    ...payload,
    metadata,
  };

  if (payload.printer_name || payload.printerName || payload.service_id || payload.serviceId) {
    return [requestPayload];
  }

  // A browser's cached flow is not authoritative for any other installation.
  // Each receiving agent resolves its own destinations and category filters.
  return [requestPayload];

  /*
  const configuredServiceIds = normalizedEventKey
    ? getConfiguredPrintEventServiceIds(normalizedEventKey, config)
    : [];

  if (!configuredServiceIds.length) {
    return [requestPayload];
  }

  const resolvedServiceIds = [...new Set(
    configuredServiceIds
      .map((serviceId) => resolveConfiguredTargetServiceId(serviceId, config))
      .filter(Boolean)
  )];

  if (!resolvedServiceIds.length) {
    return [];
  }

  return resolvedServiceIds.map((serviceId) => ({
    ...requestPayload,
    printer_name: null,
    service_id: serviceId,
  }));
  */
}

export async function sendPrintEventToAgent(payload = {}, eventKey = null, config = getCachedPrintAgentConfig()) {
  const normalizedEventKey = normalizeEventKey(eventKey);
  const metadata = payload.metadata && typeof payload.metadata === 'object'
    ? { ...payload.metadata }
    : {};

  if (normalizedEventKey) {
    metadata.print_event = normalizedEventKey;
  }

  const requests = buildPrintRequestsForEvent({
    ...payload,
    printer_name: payload.printer_name || null,
    service_id: payload.service_id || null,
    metadata,
  }, normalizedEventKey, config);
  if (!requests.length) {
    return {
      ok: false,
      accepted: false,
      printed: false,
      requiresFrontendAction: false,
      virtualActions: [],
      usedConfiguredFlow: false,
      error: 'Este flujo no tiene impresoras asignadas.',
      results: [],
    };
  }

  const response = await sendTicketToPrint(requests[0]);
  const directResults = Array.isArray(response.results) ? response.results : [response];

  return {
    ok: Boolean(response?.ok),
    transport: response?.transport,
    accepted: Boolean(response?.accepted || directResults.some((result) => result?.accepted)),
    printed: Boolean(response?.printed || directResults.some((result) => result?.printed)),
    requiresFrontendAction: Boolean(response?.requires_frontend_action),
    virtualActions: Array.isArray(response?.virtual_actions) ? response.virtual_actions : [],
    usedConfiguredFlow: response?.transport === 'restaurant' || hasConfiguredPrintFlow(eventKey, config),
    error: response?.error || directResults.find((result) => !result?.ok)?.error || '',
    results: directResults,
  };

  /*
  const requests = buildPrintRequestsForEvent(payload, eventKey, config);
  if (!requests.length) {
    return {
      ok: false,
      printed: false,
      usedConfiguredFlow: hasConfiguredPrintFlow(eventKey, config),
      error: 'No hay un destino de impresión resoluble para este flujo.',
      results: [],
    };
  }

  const results = [];

  for (const requestPayload of requests) {
    results.push(await sendTicketToPrint(requestPayload));
  }

  return {
    ok: results.length > 0 && results.every((result) => result?.ok),
    printed: results.some((result) => result?.ok),
    usedConfiguredFlow: hasConfiguredPrintFlow(eventKey, config),
    error: results.find((result) => !result?.ok)?.error || '',
    results,
  };
  */
}

function cachePrintAgentConfig(config = {}) {
  if (!isBrowser()) return;

  try {
    window.localStorage.setItem(
      `${PRINT_AGENT_CONFIG_CACHE_KEY}:${config.profile_key}`,
      JSON.stringify(normalizePrintAgentConfig(config))
    );
  } catch (error) {
    console.warn('No se pudo guardar la caché local de impresión:', error);
  }
}

export function getCachedPrintAgentConfig() {
  if (!isBrowser()) {
    return normalizePrintAgentConfig({});
  }

  try {
    const key = bindingSession.scope().profile_key;
    if (!key) return normalizePrintAgentConfig({});
    const stored = JSON.parse(window.localStorage.getItem(`${PRINT_AGENT_CONFIG_CACHE_KEY}:${key}`) || '{}');
    return normalizePrintAgentConfig(stored);
  } catch (error) {
    return normalizePrintAgentConfig({});
  }
}

function findPrintServiceById(config = {}, serviceId = null) {
  const normalizedServiceId = normalizeServiceId(serviceId);
  if (!normalizedServiceId) return null;

  return (config.print_services || []).find((service) => service?.id === normalizedServiceId) || null;
}

export function resolvePrintTarget(payload = {}, fallbackTicketType = 'generic', config = getCachedPrintAgentConfig()) {
  const normalizedConfig = normalizePrintAgentConfig(config);
  const explicitPrinterName = normalizeText(payload.printer_name || payload.printerName) || null;
  const explicitServiceId = resolveConfiguredTargetServiceId(normalizeServiceId(
    payload.service_id || payload.serviceId || payload.print_service_id || payload.printServiceId
  ), normalizedConfig);

  if (explicitPrinterName) {
    return {
      printer_name: explicitPrinterName,
      service_id: explicitServiceId,
    };
  }

  const assignments = normalizedConfig.print_service_assignments || {};
  const metadataSource = normalizeText(payload.metadata?.source || payload.source) || null;
  const ticketType = normalizeText(payload.ticket_type || payload.ticketType || fallbackTicketType) || 'generic';

  let resolvedServiceId = explicitServiceId;

  if (!resolvedServiceId && metadataSource && assignments[metadataSource]) {
    resolvedServiceId = normalizeServiceId(assignments[metadataSource]);
  }

  if (!resolvedServiceId && assignments[ticketType]) {
    resolvedServiceId = normalizeServiceId(assignments[ticketType]);
  }

  const resolvedService = findPrintServiceById(normalizedConfig, resolvedServiceId);
  const resolvedPrinterName = normalizeText(resolvedService?.printer_name) || null;
  const fallbackPrinterName = isVirtualPrinterName(normalizedConfig.printer_name)
    ? null
    : normalizedConfig.printer_name || null;

  if (resolvedServiceId) {
    return {
      printer_name: resolvedPrinterName || null,
      service_id: resolvedServiceId || null,
    };
  }

  return {
    printer_name: resolvedPrinterName || fallbackPrinterName,
    service_id: resolvedServiceId || null,
  };
}

function buildHeaders(extraHeaders = {}) {
  const headers = { ...extraHeaders };
  const token = getAgentToken();
  if (token) {
    headers['X-EWU-Agent-Token'] = token;
  }
  return headers;
}

function enqueueAgentRequest(task) {
  const nextRequest = agentRequestChain.then(task, task);
  agentRequestChain = nextRequest.catch(() => undefined);
  return nextRequest;
}

function friendlyConnectionError(error) {
  const message = String(error?.message || error || '').toLowerCase();
  if (message.includes('abort') || message.includes('timeout')) {
    return 'No se ha podido conectar con EWU Print Agent. Revisa que este instalado y abierto en este ordenador.';
  }
  if (message.includes('failed to fetch') || message.includes('network') || message.includes('cors')) {
    return 'No se ha podido conectar con EWU Print Agent. Revisa que este instalado y abierto en este ordenador.';
  }
  return 'No se ha podido conectar con EWU Print Agent. Revisa que este instalado y abierto en este ordenador.';
}

async function requestAgent(path, options = {}) {
  const {
    method = 'GET',
    body,
    timeoutMs = DEFAULT_TIMEOUT_MS,
    headers = {},
  } = options;

  const run = path.startsWith('/binding/') ? (task) => task() : enqueueAgentRequest;
  return run(async () => {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), timeoutMs);

    try {
      const response = await fetch(`${PRINT_AGENT_BASE_URL}${path}`, {
        method,
        cache: 'no-store',
        mode: 'cors',
        signal: controller.signal,
        keepalive: path === '/binding/revoke',
        headers: buildHeaders({
          ...(await localViewHeaders(path)),
          ...(body ? { 'Content-Type': 'application/json' } : {}),
          ...headers,
          'X-EWU-Control-Token': bindingSession.scope().control_token || '',
        }),
        ...(body ? { body: JSON.stringify(body) } : {}),
      });

      const contentType = response.headers.get('content-type') || '';
      const payload = contentType.includes('application/json')
        ? await response.json()
        : { ok: response.ok, message: await response.text() };

      if (!response.ok) {
        return {
          ok: false,
          error: payload?.error || payload?.detail || payload?.message || 'No se pudo completar la peticion al agente.',
          status: response.status,
        };
      }

      return payload;
    } catch (error) {
      return {
        ok: false,
        error: friendlyConnectionError(error),
        detail: error?.message || String(error),
      };
    } finally {
      window.clearTimeout(timeout);
    }
  });
}

export async function getPrintAgentStatus() {
  const response = await requestAgent('/status');
  if (!response?.ok) {
    return {
      ok: false,
      connected: false,
      service: 'EWU Print Agent',
      version: null,
      device_name: null,
      error: response?.error || 'No se ha podido conectar con EWU Print Agent.',
    };
  }

  return {
    ok: true,
    connected: Boolean(response.connected !== false),
    service: response.service || 'EWU Print Agent',
    version: response.version || null,
    device_name: response.device_name || null,
    installation_id: response.installation_id || null,
  };
}

export async function getLocalPrinters() {
  const response = await requestAgent('/printers');
  if (!response?.ok) {
    return {
      ok: false,
      connected: false,
      printers: [],
      error: response?.error || 'No se pudieron listar las impresoras.',
    };
  }

  return {
    ok: true,
    connected: true,
    printers: Array.isArray(response.printers) ? response.printers : [],
  };
}

// mode: null (prueba normal), 'beep' (con el pitido de la impresora), 'characters' (tabla de caracteres)
// o 'width' (regla con los caracteres por línea del servicio).
export async function sendTestPrint(selection = null, mode = null) {
  const printerName = typeof selection === 'string'
    ? (normalizeText(selection) || null)
    : (normalizeText(selection?.printer_name || selection?.printerName) || null);
  const serviceId = typeof selection === 'string'
    ? null
    : normalizeServiceId(selection?.service_id || selection?.serviceId);

  const response = await requestAgent('/test-print', {
    method: 'POST',
    body: {
      printer_name: printerName,
      service_id: serviceId,
      ...(mode ? { mode } : {}),
    },
  });

  if (!response?.ok) {
    return {
      ok: false,
      error: response?.error || 'No se ha podido imprimir el ticket de prueba.',
    };
  }

  publishVirtualPrintActions(response.virtual_actions);

  return {
    ok: true,
    message: response.message || 'Ticket de prueba enviado a impresion',
    accepted: Boolean(response.accepted),
    printed: Boolean(response.printed),
    requiresFrontendAction: Boolean(response.requires_frontend_action),
    virtualActions: Array.isArray(response.virtual_actions) ? response.virtual_actions : [],
  };
}

export async function sendTicketToPrint(payload = {}) {
  if (usesRestaurantPrintTransport(payload)) {
    return { ...(await submitRestaurantPrint(payload)), transport: 'restaurant' };
  }
  const hasExplicitTarget = Boolean(
    normalizeText(payload.printer_name || payload.printerName)
    || normalizeServiceId(payload.service_id || payload.serviceId || payload.print_service_id || payload.printServiceId)
  );
  const isAgentResolvedEvent = Boolean(normalizeEventKey(payload.metadata?.print_event)) && !hasExplicitTarget;
  const target = isAgentResolvedEvent
    ? { printer_name: null, service_id: null }
    : resolvePrintTarget(payload, payload.ticket_type || 'generic');
  await localViewHeaders('/print');
  const metadata = { ...payload.metadata, restaurant_id: payload.metadata?.restaurant_id || viewContext?.restaurant_id || '' };
  if (typeof metadata.print_beep !== 'boolean') {
    metadata.print_beep = cachedPrintBeepFor(metadata.restaurant_id, metadata.print_event);
  }
  if (!metadata.print_code_page) {
    metadata.print_code_page = cachedPrintCodePageFor(metadata.restaurant_id);
  }
  if (!metadata.print_command_layout) {
    metadata.print_command_layout = cachedPrintCommandLayoutFor(metadata.restaurant_id);
  }
  if (typeof metadata.print_command_service_label !== 'string') {
    metadata.print_command_service_label = cachedPrintCommandServiceLabelFor(metadata.restaurant_id);
  }
  Object.entries(cachedPrintCommandDesignFor(metadata.restaurant_id)).forEach(([key, value]) => {
    if (metadata[key] === undefined) metadata[key] = value;
  });
  const response = await requestAgent('/print', {
    method: 'POST',
    body: {
      printer_name: target.printer_name,
      service_id: target.service_id,
      ticket_type: payload.ticket_type || 'generic',
      content: payload.content || '',
      html_content: payload.html_content || payload.htmlContent || '',
      metadata,
    },
  });

  if (!response?.ok) {
    return {
      ok: false,
      error: response?.error || 'No se ha podido enviar el ticket a impresion.',
    };
  }

  publishVirtualPrintActions(response.virtual_actions);

  return {
    ok: true,
    message: response.message || 'Ticket enviado a impresion',
    accepted: Boolean(response.accepted ?? response.ok),
    printed: Boolean(response.printed ?? response.ok),
    requires_frontend_action: Boolean(response.requires_frontend_action),
    virtual_actions: Array.isArray(response.virtual_actions) ? response.virtual_actions : [],
    job_id: response.job_id || null,
    printer_name: response.printer_name || target.printer_name || null,
    service_id: response.service_id || target.service_id || null,
    results: Array.isArray(response.results) ? response.results : [],
  };
}

export async function getPrintAgentConfig(options = {}) {
  const response = options.statusOnly
    ? await requestAgent('/config')
    : await bindingSession.select(process.env.VUE_APP_API_URL, getPartnerToken());
  if (!response?.ok) {
    return {
      ok: false,
      error: response?.error || 'No se pudo leer la configuracion del agente.',
      config: null,
    };
  }

  const config = normalizePrintAgentConfig({
    ...(response.config && typeof response.config === 'object' ? response.config : {}),
  });
  if (!options.statusOnly) cachePrintAgentConfig(config);

  return {
    ok: true,
    config,
    restaurant_name: response.restaurant_name || '',
  };
}

export async function savePrintAgentConfig(config = {}, options = {}) {
  if (!bindingSession.scope().control_token) {
    const selected = await bindingSession.select(process.env.VUE_APP_API_URL, getPartnerToken());
    if (!selected.ok) return selected;
    if (config.profile_key !== selected.config.profile_key) {
      return { ok: false, error: 'El restaurante cambio. Recarga los ajustes antes de guardar.' };
    }
  }
  if (config.profile_key !== bindingSession.scope().profile_key) {
    return { ok: false, error: 'Los ajustes pertenecen a otro perfil. Recargalos antes de guardar.' };
  }
  const normalizedConfig = normalizePrintAgentConfig(config);
  const response = await requestAgent('/config', {
    method: 'PUT',
    timeoutMs: Number(options.timeoutMs) > 0 ? Number(options.timeoutMs) : DEFAULT_TIMEOUT_MS,
    body: {
      ...bindingSession.scope(),
      printer_name: normalizedConfig.printer_name,
      print_services: normalizedConfig.print_services,
      print_service_assignments: normalizedConfig.print_service_assignments,
      print_event_rules: normalizedConfig.print_event_rules,
      command_category_assignments: normalizedConfig.command_category_assignments,
      auto_print_enabled: normalizedConfig.auto_print_enabled !== false,
      restaurant_name: normalizedConfig.restaurant_name || '',
      cash_drawer: normalizedConfig.cash_drawer,
    },
  });

  if (!response?.ok) {
    return {
      ok: false,
      error: response?.error || 'No se pudo guardar la configuracion del agente.',
    };
  }

  const nextConfig = normalizePrintAgentConfig({
    ...normalizedConfig,
    ...(response.config && typeof response.config === 'object' ? response.config : {}),
  });
  cachePrintAgentConfig(nextConfig);

  return {
    ok: true,
    config: nextConfig,
  };
}

export async function syncPrintAgentPartnerToken(partnerToken) {
  return bindingSession.sessionChanged(partnerToken);
}

export async function openLocalCashDrawer(actionId = null) {
  const response = await requestAgent('/cash-drawer/open', {
    method: 'POST',
    body: { action_id: normalizeText(actionId) || null },
  });
  return response?.ok
    ? response
    : { ok: false, opened: false, error: response?.error || 'No se pudo abrir la caja.' };
}

export async function synchronizePrintAgentSession({ partnerToken = '' } = {}) {
  if (partnerToken && partnerToken !== 'null') {
    // Keeps the restaurant beep setting for direct prints in local mode.
    getRestaurantPrintSettings(process.env.VUE_APP_API_URL, partnerToken).catch(() => undefined);
  }
  return bindingSession.sessionChanged(partnerToken);
}

export async function getPendingVirtualPrintActions() {
  const response = await requestAgent('/virtual-actions?status=pending', {
    timeoutMs: 1800,
  });
  if (!response?.ok) {
    return {
      ok: false,
      actions: [],
      error: response?.error || 'No se pudieron consultar las impresiones virtuales pendientes.',
    };
  }
  return {
    ok: true,
    actions: Array.isArray(response.actions) ? response.actions : [],
    scope_key: response.scope_key || '',
  };
}

export async function acknowledgeVirtualPrintAction(actionId) {
  const normalizedActionId = normalizeText(actionId);
  if (!normalizedActionId) {
    return { ok: false, error: 'La accion virtual no tiene identificador.' };
  }
  return requestAgent(`/virtual-actions/${encodeURIComponent(normalizedActionId)}/ack`, {
    method: 'POST',
  });
}

export async function getPrintAgentDiagnostics() {
  const response = await requestAgent('/diagnostics', {
    timeoutMs: 10000,
  });
  if (!response?.ok) {
    return {
      ok: false,
      diagnostics: null,
      error: response?.error || 'No se pudo obtener el diagnostico del agente.',
    };
  }
  return {
    ok: true,
    diagnostics: response,
  };
}

export async function cleanupPrintAgentHistory(retentionDays = 14) {
  return requestAgent(`/maintenance/cleanup?retention_days=${Math.max(1, Number(retentionDays) || 14)}`, {
    method: 'POST',
    timeoutMs: 10000,
  });
}

const bindingSessionId = (typeof crypto !== 'undefined' && crypto.randomUUID?.()) || 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
  const n = Math.floor(Math.random() * 16); return (c === 'x' ? n : (n & 3) | 8).toString(16);
});
const bindingSession = createPrintAgentBinding({
  request: requestAgent, sessionId: bindingSessionId,
  readToken: () => getPartnerToken(),
  schedule: (callback, delay) => window.setTimeout(callback, delay),
  cancel: (timer) => window.clearTimeout(timer),
});
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => { void bindingSession.stop(); });
  window.addEventListener('ewu-partner-session-changed', () => {
    void bindingSession.sessionChanged(getPartnerToken());
  });
}
export const selectPrintAgentProfile = (partnerToken) => bindingSession.select(process.env.VUE_APP_API_URL, partnerToken);
export const activatePrintAgentBinding = (mode, partnerToken) => bindingSession.activate(mode, partnerToken);
export const deactivatePrintAgentBinding = () => bindingSession.deactivate();
export const importLegacyPrintAgentProfile = () => requestAgent('/binding/import-legacy', {
  method: 'POST', body: bindingSession.scope(),
});

let viewContext = null;
let viewToken = null;
let viewRequest = null;
async function localViewHeaders(path) {
  if (!['/virtual-actions?status=pending', '/cash-drawer/open', '/print'].includes(path)) return {};
  const localMode = ['LOCAL_MODE', 'LOCAL_MODE_CLOUD_REACHABLE'].includes(restaurantPrintMode());
  if (localMode) {
    const session = isOfflineSessionActive() ? getOfflineSession() : null;
    let restaurantId = session?.user_id ? session.restaurant_id : null;
    if (!restaurantId) {
      // Preserve the already-authorized online session during Local Hub operation,
      // but never take another user's cached restaurant after switching accounts.
      try {
        const tokenPayload = getPartnerToken().split('.')[1];
        const claims = JSON.parse(atob(tokenPayload.replace(/-/g, '+').replace(/_/g, '/')));
        const cached = getPartnerSessionCache();
        if (claims.id && claims.id === cached?.id) restaurantId = cached.restaurant_id;
      } catch { /* An unlocked offline session or explicit job scope is required. */ }
    }
    viewContext = restaurantId ? { restaurant_id: String(restaurantId), offlineContext: true } : null;
    return { 'X-EWU-Restaurant-ID': viewContext?.restaurant_id || '',
      'X-EWU-Backend-URL': process.env.VUE_APP_API_URL.replace(/\/+$/, '') };
  }
  if (viewContext?.offlineContext) viewContext = null;
  const token = getPartnerToken();
  if (token !== viewToken) { viewToken = token; viewContext = null; viewRequest = null; }
  if (token && token !== 'null' && !viewContext && !viewRequest) {
    const expected = token;
    const contextController = new AbortController();
    const contextTimeout = window.setTimeout(() => contextController.abort(), 4000);
    viewRequest = fetch(`${process.env.VUE_APP_API_URL}/v1/api/app/print-agents/view-context`, {
      headers: { Authorization: `Bearer ${token}` }, signal: contextController.signal,
    }).then(async (response) => {
      const context = response.ok ? await response.json() : null;
      if (viewToken === expected) viewContext = context;
    }).catch(() => {}).finally(() => {
      window.clearTimeout(contextTimeout);
      if (viewToken === expected) viewRequest = null;
    });
  }
  if (viewRequest) await viewRequest;
  return { 'X-EWU-Restaurant-ID': viewContext?.restaurant_id || '',
    'X-EWU-Backend-URL': process.env.VUE_APP_API_URL.replace(/\/+$/, '') };
}
